/**
 * @class Tracking
 * @module Ladies
 *
 * Die Tracking-Klasse bietet Funktionalitäten zum Melden von
 * 	Tracking-Ereignissen an die Logging-API.
 */

import { hasOwn } from "@modules/util/has-own"

const TRACKING_INDEX_NAME = "tracking"

export function sendTrackingEvent(message: string, extraData: Record<string, unknown> = {}): void {
    if (!SiteInfo.isTrackingLoggingApiActive) {
        return
    }

    const payload = {
        error: {},
        extraData: {
            domain: siteConfig.trackingParameters?.domain ?? "",
            isMobile: SiteInfo.isMobileVersion,
            userGroup: userData?.userType ?? "guest",
            ...extraData,
        },
        message,
        url: window.location.href,
        loginId: String(userData?.userId ?? 0),
        commitSha: siteConfig.commitSha,
        version: siteConfig.version,
        timestamp: new Date().toISOString(),
        browserLocale: navigator.language.slice(0, 2).toUpperCase(),
        userAgent: navigator.userAgent,
        webdriver: navigator.webdriver ?? false,
    }

    fetch(`https://logs.ladies.de/api/v1/${TRACKING_INDEX_NAME}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    }).catch(() => {})
}

export const trackBannerClick = (trackingClickUrl: string) => {
    const rawUrl = trackingClickUrl.replace("_time_", Date.now().toString())

    // Legacy Tracking-Pixel wird parallel zum neuen Logging-API-Aufruf erstellt
    const trImg = document.createElement("img")
    trImg.setAttribute("src", rawUrl)
    trImg.setAttribute("width", "1")
    trImg.setAttribute("height", "1")
    window.document.body.appendChild(trImg)

    if (!SiteInfo.isTrackingLoggingApiActive) {
        return
    }

    const url = new URL(rawUrl)
    const extraData = Object.fromEntries(url.searchParams.entries())

    sendTrackingEvent("banner click", { type: url.pathname.replace(/^\//, ""), ...extraData })
}

export class TrackingClass {
    /**
     * @constructor
     */
    constructor() {
        // Automatisches Tracken für Links mit bestimmtem data-*-Attribut.
        // 	Die TrackingId besteht aus kommagetrennter AuftragsID und Place.
        // 	Beispiel:  <a href="#" data-tracking-id="9432545,234342">
        $(document).on("click", "a[data-tracking-id]", this.track.bind(this))
    }

    /**
     * Zeitstempel zum aushebeln des Caches (Legacy-Pixel)
     *
     * @private
     * @type {Number}
     */
    trackingTime = 0

    /**
     * Der Name des aktuell geladenen Produktes (z.Bsp. ladies), für das Legacy-Pixel
     *
     * @private
     * @type {String}
     */
    product = ""

    /**
     * Ein Objekt mit den Keys und Werten, welche beim Erstellen des
     * 	Pixels in URL-Query-Parameter umgewandelt werden
     *
     * @private
     * @type {Object}
     */
    parameters = {}

    /**
     * Schlüssel bereits gemeldeter Tracking-Ereignisse (type-a{auftragsid}-p{place}),
     * 	um Mehrfachmeldungen für dasselbe Advert+Place zu verhindern
     *
     * @private
     */
    private trackedKeys = new Set<string>()

    /**
     * Aufgeschobene Tracking-Aufrufe (loadImmediately=false), die erst
     * 	via flushDeferredTracking() tatsächlich gemeldet werden
     *
     * @private
     */
    private deferredEvents: Array<() => void> = []

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackRedirect() {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.bannerlink, "redirect link click")
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackEinzelansicht() {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.einzelansicht, "single advert view")
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackNews() {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.news, "news click")
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackAnzeigenlink() {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.anzeigenlink, "advert link click")
    }

    /**
     * Tracke über BezugsID verknüpfte Anzeigen
     *
     * @public
     */
    trackBezugslink(auftragsid: string, place: string) {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.einzelansicht, "referenced advert view", {
            auftragsid: auftragsid,
            place: place,
        })
    }

    /**
     * Aufrufen, um Galerie-Klicks zu tracken
     *
     * @public
     */
    trackGallery(newTag: string | null = null) {
        let tag = newTag
        if (!tag) {
            tag = siteConfig.trackingTagsMap.bildansicht
        }
        this.generateTrackingPixel(tag, "gallery image click")
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     * @param {number} auftragsid
     * @param {number} place
     * @param {boolean} loadImmediately
     */
    trackPushedAdvertEinblendung(auftragsid: string | number, place: string | number) {
        this.generateTrackingPixel(
            siteConfig.trackingTagsMap.pushedadvert,
            "pushed advert impression",
            {
                auftragsid: auftragsid,
                place: place,
            },
            false,
        )
    }

    trackBannerEinblendung(auftragsid: string | number, place: string | number, loadImmediately = true) {
        this.generateTrackingPixel(
            siteConfig.trackingTagsMap.einblendung,
            "banner impression",
            {
                auftragsid: auftragsid,
                place: place,
            },
            false,
            loadImmediately,
        )
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackBannerlink(auftragsid: string, place: string) {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.bannerlink, "banner link click", {
            auftragsid: auftragsid,
            place: place,
        })
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackNewslink(auftragsid: string, place: string) {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.einzelansicht, "news link click", {
            auftragsid: auftragsid,
            place: place,
        })
    }

    /**
     * Aufrufen, um Telefonsex zu tracken
     *
     * @public
     * @param {Number} [auftragsid]
     */
    trackTelefonsex(auftragsid: string) {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.einzelansicht, "telefonsex link click", {
            auftragsid: auftragsid,
            publikation: "Telefonsex",
        })
    }

    /**
     * Generiert automatisch ein Tracking-Pixel für den geklickten Link
     *
     * @private
     * @param {Event} clickEvent
     *
     * @see trackBezugslink
     */
    track(clickEvent: MouseEvent) {
        const trackingLinkElement = $(clickEvent.currentTarget)
        const trackingId = trackingLinkElement.data("trackingId").split(",")
        const trackingType = trackingLinkElement.data("trackingType") || "bannerlink"
        // Notwendige Haupt-Attribute
        const auftragsid = trackingId[0]
        const place = trackingId[1]

        // Generiere Methodenaufruf aus dem Tracking-Typ
        const trackingCall = "track".concat(
            trackingType.replace(/^(\w)/, (firstLetter: string) => firstLetter.toUpperCase()),
        )

        // Rausspringen, wenn der Tracking-Typ nicht existiert
        if (!(trackingCall in this)) {
            return
        }

        // Da es sich hier um einen dynamischen Aufruf handelt, bestehende
        // 	Config zwischenspeichern, da ein Link nicht unbedingt die
        // 	aktuelle Seite wechseln muss und ansonsten bei weiteren
        // 	Tracking-Aufrufen die Werte verfälscht sein könnten
        const keepTrackingParameters = siteConfig.trackingParameters

        try {
            // Mit try-catch, damit auch bei Fehlschlag die alte
            // 	Config wiederhergestellt werden kann
            this.parseTrackingParameters(trackingLinkElement)
            // @ts-expect-error old cold and before is a check if tracking call in this
            this[trackingCall](auftragsid, place)
        } catch (e) {}

        // Alte Config wiederherstellen
        siteConfig.trackingParameters = keepTrackingParameters
    }

    /**
     * Ersetzt die Config-Properties mit den Angaben der data-*-Attribute
     * 	des aktuell angeklickten Links. Diese werden später zurückgesetzt.
     *
     * @private
     * @param {jQuery|Object} trackElement
     */
    parseTrackingParameters(trackElement: typeof $) {
        const data = trackElement.data()
        const allowedKeys = ["domain", "name", "publikation", "rubrik"]

        siteConfig.trackingParameters = siteConfig.trackingParameters || {}

        for (const key in data) {
            if (!hasOwn(data, key) || allowedKeys.indexOf(key) < 0) {
                continue
            }

            // @ts-expect-error old cold, check beforehand if key exists
            siteConfig.trackingParameters[key] = data[key]
        }
    }

    /**
     * Meldet ein Tracking-Ereignis parallel an das Legacy-Pixel-System und die neue Logging-API
     *
     * @private
     * @param {String} type
     * @param {String} message Beschreibt die Art der Interaktion, z.B. "click", "view", "impression"
     * @param {Object} [extraParameters]
     * @param {Boolean} replaceOldTag
     * @param {Boolean} loadImmediately
     */
    generateTrackingPixel(
        type: string,
        message: string,
        extraParameters: object = {},
        replaceOldTag = true,
        loadImmediately = true,
    ): void {
        const oldTracker = replaceOldTag ? $(".tracker") : []

        this.prepareUrlValues()

        // Falls optionale weitere Parameter übergeben wurden, diese hier
        // 	dem Parameter-Objekt hinzufügen
        $.extend(this.parameters, extraParameters)

        if (!this.parametersValidated(this.parameters)) {
            console.error(
                "Tracking-Parameter konnten nicht validiert werden. Tracking-Ereignis wird nicht gemeldet.",
                this.parameters,
            )
            return
        }

        const liveParameters = this.parameters as Record<string, unknown>
        const trackedKey = this.trackedKey(type, liveParameters.auftragsid, liveParameters.place)
        this.trackedKeys.add(trackedKey)

        // Nur für die neue Logging-API relevant, deshalb hinter dem Feature-Flag. Die Kopie muss
        // hier (vor generateTrackingUrl()) erstellt werden, da diese this.parameters mutiert (tmp-Wert),
        // und VOR dem eigentlichen Feuern (auch im deferred-Fall), damit keine späteren Tracking-Aufrufe
        // zwischenzeitlich this.parameters überschreiben
        const params: Record<string, unknown> | null = SiteInfo.isTrackingLoggingApiActive
            ? { ...this.parameters }
            : null
        if (params) {
            this.cleanParameters(params as Record<string, string>)
        }

        const trackingUrl = this.generateTrackingUrl(type)

        // Legacy Tracking-Pixel wird erstellt (parallel zum neuen Logging-API-Aufruf)
        const trackerPixel = $("<img />")
            .prop("width", 1)
            .prop("height", 1)
            .css("display", "none")
            .addClass("tracker")
            .addClass(`t-${type}-a`.concat(Array.from(Object.values(extraParameters)).join("-p")))

        if (!oldTracker.length) {
            $("body").append(trackerPixel)
        } else {
            oldTracker.replaceWith(trackerPixel)
        }

        const fireEvent = (): void => {
            trackerPixel.prop("src", trackingUrl)

            if (params) {
                sendTrackingEvent(message, { type, ...params })
            }
        }

        if (loadImmediately) {
            fireEvent()
        } else {
            this.deferredEvents.push(fireEvent)
        }
    }

    private trackedKey(type: string, advertId: unknown, place: unknown): string {
        return `${type}-a${advertId}-p${place}`
    }

    /**
     * Prüft, ob für dieses Advert+Place bereits ein Tracking-Ereignis
     * 	gemeldet oder zur Meldung vorgemerkt wurde
     *
     * @public
     */
    isAlreadyTracked(type: string, advertId: string | number, place: string | number): boolean {
        return this.trackedKeys.has(this.trackedKey(type, advertId, place))
    }

    /**
     * Meldet alle aufgeschobenen Tracking-Ereignisse (siehe loadImmediately=false)
     *
     * @public
     */
    flushDeferredTracking(): void {
        for (const fireEvent of this.deferredEvents) {
            fireEvent()
        }
        this.deferredEvents = []
    }

    isNumeric(value: any): boolean {
        return !Number.isNaN(value) && !Number.isNaN(Number.parseFloat(value))
    }

    parametersValidated(parameters: any): boolean {
        const requiredParameters = ["place", "auftragsid", "domain"]

        for (const element of requiredParameters) {
            if (!parameters[element]) {
                return false
            }
        }

        if (!this.isNumeric(parameters.place)) {
            return false
        }

        if (!this.isNumeric(parameters.auftragsid)) {
            return false
        }

        return parameters.domain !== ""
    }

    /**
     * Setzt bestimmte, von der URL benötigte Werte auf Instanzvariablen
     *
     * @private
     */
    prepareUrlValues() {
        this.trackingTime = new Date().getTime()
        this.product = siteConfig.trackingName
        this.parameters = siteConfig.trackingParameters || {}
    }

    /**
     * Generiert die URL, die dem Legacy-Pixel als Source zugewiesen wird
     *
     * @private
     * @param {String} type
     * @return {String}
     */
    generateTrackingUrl(type: string) {
        const urlBase = `${siteConfig.trackingPath + this.product}_${type}`
        const urlParameters = $.extend(this.parameters, { tmp: this.trackingTime })

        this.cleanParameters(urlParameters)

        // Umwandlung des Objektes in einen URL-Query-String via jQuery
        return `${urlBase}?${$.param(urlParameters)}`
    }

    /**
     * Entfernt alle nicht gesetzten Properties aus dem Parameter-Objekt
     *
     * @private
     * @param {Object} parameters
     */
    cleanParameters(parameters: Record<string, string>) {
        for (const key of Object.keys(parameters)) {
            // Nur in diesen Fällen das Property entfernen
            if (undefined === parameters[key] || null === parameters[key]) {
                delete parameters[key]
            }

            if (
                ["{__REPLACE_TRACKING_ADVERT_NAME__}", "{__REPLACE_TRACKING_CATEGORY__}"].indexOf(parameters[key]) >= 0
            ) {
                delete parameters[key]
            }

            // Leerstring, Integer 0 und Boolean false werden beibehalten
        }
    }
}

window.trackBannerClick = trackBannerClick
window.sendTrackingEvent = sendTrackingEvent
window.Tracking = new TrackingClass()

// Events queued by inline page scripts that ran before this bundle finished loading (script type="module" is deferred)
for (const [message, extraData] of window.deferredTrackingEvents ?? []) {
    sendTrackingEvent(message, extraData)
}
window.deferredTrackingEvents = []
